<script setup lang="ts">
import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { vueComponentToggler } from '../../../modules/toggler_wp';
import { useCartSummary } from '../../composables/useCartSummary';
import { useOrderTypeToggle } from '../../composables/useOrderTypeToggle';
import { useStockValidation } from '../../composables/useStockValidation';
import { useCartPageUpsell } from '../../composables/useUpsell';
import { CheckoutStages } from '../../services/models/checkout.models';
import { resolveOutOfStockMessage } from '../../utils/checkoutHelpers';
import { formatCurrency } from '../../utils/currencyHelpers';
import { formatWithoutRounding } from '../../utils/productPrice';
import ProductsModuleContent from '../../views/productsModule/productsModuleContent.vue';
import { useCheckoutStateStore } from '../checkout/stores/checkoutStateStore';
import CheckoutLoginCta from './checkoutLoginCta.vue';
import LineItem from './lineItem.vue';
import LineItemSkeleton from './lineItemSkeleton.vue';
import Accordion from '../accordion/accordion.vue';
import ShoppingCartDiscounts from './shoppingCartDiscounts.vue';
import ShoppingCartKsps from './shoppingCartKsps.vue';
import ShoppingCartSummary from './shoppingCartSummary.vue';
import StockSummary from './StockSummary.vue';
import { useShoppingCartStore } from './stores/shoppingCartStore';
import { useClickAndCollectLocationStore } from './stores/clickAndCollectLocationStore';
import { useOrderTypeStore } from './stores/orderTypeStore';
import TopTextSkeleton from './topTextSkeleton.vue';
import ToggleButtonGroup from '../form/ToggleButtonGroup.vue';
import ClickAndCollectPanel from './ClickAndCollectPanel.vue';

const props = defineProps<{
  ksps: string | null; // JSON Content from NewCheckoutIndex.cshtml
  maxCartSize?: string;
  initialLineItemCount?: string; // Number of line items in the cart when the component is mounted
  isQuickAdd?: boolean;
  showInventoryNotification?: boolean;
}>();

const MOBILE_UPSELL_DISPLAY_TRESHOLD = 1023;
const shoppingCartStore = useShoppingCartStore();
const checkoutStateStore = useCheckoutStateStore();
const orderTypeStore = useOrderTypeStore();
const clickAndCollectLocationStore = useClickAndCollectLocationStore();
const { totalAmount } = useCartSummary();
const INITIAL_LINE_ITEM_COUNT = ref(Number(props.initialLineItemCount) || 3);
const MAX_CART_SIZE = ref(Number(props.maxCartSize) || 99);

const orderTypeOptions = [
  { value: 'online', label: 'Online' },
  { value: 'clickAndCollect', label: 'Klikk og hent' },
];

const clickAndCollectPanelRef = ref<InstanceType<typeof ClickAndCollectPanel> | null>(null);
const stockSummaryRef = ref<InstanceType<typeof StockSummary> | null>(null);

const isClickAndCollectActive = computed(() => {
  return checkoutStateStore.clickAndCollectEnabled && orderTypeStore.isClickAndCollect;
});

const { handleOrderTypeChange } = useOrderTypeToggle();
const { verifyStock } = useStockValidation();

// Owns the submit-time stock re-check for both order types —
// `shoppingCartSummary.vue` only renders the buttons/busy-state and calls
// straight through to these via props, so this is the one place that
// decides what to do with the result (navigate, submit the click & collect
// form, or focus `StockSummary`).
const handleGoToCheckout = async () => {
  const stockSufficient = await verifyStock();
  if (stockSufficient) {
    await checkoutStateStore.goToStage(CheckoutStages.Checkout);
  } else {
    void stockSummaryRef.value?.focusSummary?.();
  }
};

const handleConfirmClickAndCollect = async () => {
  const stockSufficient = await verifyStock();
  clickAndCollectPanelRef.value?.handleSubmit({ stockSufficient });
  if (!stockSufficient) {
    void stockSummaryRef.value?.focusSummary?.();
  }
};

watch(
  () => shoppingCartStore.cart?.itemCount,
  (newVal) => {
    localStorage.setItem('totalProductCount', JSON.stringify(newVal));
  },
  { deep: true }
);

const mobileUpsellVisible = ref(window.innerWidth < MOBILE_UPSELL_DISPLAY_TRESHOLD);
const handleResize = () => {
  mobileUpsellVisible.value = window.innerWidth < MOBILE_UPSELL_DISPLAY_TRESHOLD;
};

/**
 * Computes the current size of the shopping cart. (Adds together all quantity values of lineItems)
 *
 * @returns {number} The current size of the shopping cart.
 */
const currentCartSize = computed(() => {
  return shoppingCartStore.currentCartSize;
});

/**
 * Determines if the shopping cart is full.
 * @returns {boolean} True if the shopping cart is full, false otherwise.
 */
const cartIsFull = computed(() => {
  if (!currentCartSize.value) return false;
  return currentCartSize.value >= MAX_CART_SIZE.value;
});

/**
 * Computed property for the top text in the shopping cart view on mobile viewports.
 * Conditionally contains shipping cost if the cart qualifies for free shipping.
 *
 * @returns {string} The top text value.
 */
const topText = computed(() => {
  if (!shoppingCartStore.cart) return;

  const baseText = `${currentCartSize.value} varer / Totalsum: ${shoppingCartStore.cart.total && formatCurrency(shoppingCartStore.cart.total.currency)} ${shoppingCartStore.cart.total && formatWithoutRounding(totalAmount.value)}`;
  return shoppingCartStore.freeShipping ? `${baseText} (Fri frakt)` : baseText;
});

/**
 * If cartIsFull becomes false, remove the summaryError value.
 *
 * @param {boolean} newValue - The new value of the 'cartIsFull' property.
 */
watch(cartIsFull, (newValue) => {
  if (!newValue) {
    shoppingCartStore.setSummaryError('');
  }
});

/**
 * Clears the discountError value.
 */
const handleResetDiscountError = () => {
  shoppingCartStore.setDiscountError('');
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  vueComponentToggler();
  // Resolves click & collect store availability (feature toggle + a store
  // existing) so `orderTypeStore.canUseClickAndCollect` is known before the
  // order-type toggle would otherwise render, instead of the toggle being
  // stuck hidden because nothing has fetched the store list yet.
  orderTypeStore.initClickAndCollectAvailability();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

const variantKeys = computed(() => {
  const cart = shoppingCartStore.cart;
  return cart && cart.lineItems ? cart.lineItems.map((lineItem) => lineItem.esalesKey) : [];
});

const { upsellModel, isLoading } = useCartPageUpsell({
  variantKeys,
  trigger: variantKeys,
});
</script>

<template>
  <div>
    <div class="layout-new-checkout__top">
      <h1>{{ $t('shoppingCart') }}</h1>
      <div class="layout-new-checkout__top-content">
        <span v-if="topText && !checkoutStateStore.isLoading" class="layout-new-checkout__top-text">{{ topText }}</span>
        <TopTextSkeleton v-if="checkoutStateStore.isLoading" class="layout-new-checkout__top-text" />
      </div>
    </div>
    <div class="layout-new-checkout">
      <div class="layout-new-checkout__heading">
        <h1>{{ $t('shoppingCart') }}</h1>
      </div>
      <div class="layout-new-checkout__main">
        <div class="layout-new-checkout__vertical-split">
          <div class="layout-new-checkout__primary layout-new-checkout__element">
            <CheckoutLoginCta v-if="
              !checkoutStateStore.isLoadingCart &&
              shoppingCartStore.checkoutLoginCta?.enabled &&
              !shoppingCartStore.cart?.isAuthenticated
            " :heading="shoppingCartStore.checkoutLoginCta.heading"
              :content="shoppingCartStore.checkoutLoginCta.content" />
            <div class="layout-new-checkout__primary-content layout-new-checkout__primary-content--shoppingcart">
              <h2 class="sr-only">{{ $t('products') }}</h2>
              <StockSummary ref="stockSummaryRef" />
              <ul v-auto-animate class="checkout-line-items">
                <LineItem v-for="lineItem in shoppingCartStore.cart?.lineItems" :key="lineItem.ean"
                  :line-item="lineItem" :out-of-stock="lineItem.currentInventory <= 0"
                  :error="lineItem.currentInventory <= 0 ? resolveOutOfStockMessage(orderTypeStore.isClickAndCollect, clickAndCollectLocationStore.storeName) : shoppingCartStore.lineItemErrors?.[lineItem.ean]"></LineItem>
              </ul>
              <!-- Only the initial full-page cart load shows skeletons; an
                   order-type-toggle-triggered refetch (isLoadingCart alone)
                   keeps existing line items visible with no flicker. -->
              <div v-if="checkoutStateStore.isLoadingCart && checkoutStateStore.isLoading" class="checkout-line-items">
                <LineItemSkeleton v-for="(_, index) in INITIAL_LINE_ITEM_COUNT" :key="index"></LineItemSkeleton>
              </div>
              <template v-if="
                !shoppingCartStore.lineItemsInStock?.length &&
                !shoppingCartStore.lineItemsOutOfStock?.length &&
                !checkoutStateStore.isLoading &&
                !checkoutStateStore.isLoadingCart
              ">
                <h3 class="layout-new-checkout__primary-placeholder">
                  {{ $t('shoppingCartEmpty') }}
                </h3>
              </template>
            </div>
            <template v-for="(upsellItem, index) in upsellModel" :key="index">
              <ProductsModuleContent v-if="upsellItem.primaryList" :key="index" v-show="!mobileUpsellVisible"
                :heading="upsellItem.primaryList.label ?? $t('maybeYouAlsoLike')"
                :search-result-recommend-list="upsellItem"
                :user-authenticated="shoppingCartStore.cart?.isAuthenticated ?? false" :favorite-form-hidden="true"
                :is-loading="isLoading" :hide-sizes="true" :hide-colors="true" :hide-favorites="true"
                :is-quick-add="props.isQuickAdd" :productCount="upsellItem.primaryList.productGroups.length"
                :use-skeleton-loaders="true" :track-view-item-list="!mobileUpsellVisible" :track-select-item="true"
                :item-list-id="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :item-list-name="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :upsell-algorithm="upsellItem.primaryList.algorithm"
                container-class="product-list__upsell-small product-list__upsell-small--desktop"
                :use-slider-for-desktop="true" :teaser-product-count="4" :show-inventory-notification="props.showInventoryNotification" />
            </template>
          </div>
          <div class="layout-new-checkout__secondary">
            <!-- Order type toggle (Click and Collect feature). Gated only on
                 canUseClickAndCollect (feature toggle + a store resolved) -
                 NOT on lineItemsInStock, since a cart with zero stock at the
                 selected click & collect store must still let the customer
                 switch back to online instead of trapping them. -->
            <div v-if="orderTypeStore.canUseClickAndCollect && shoppingCartStore.hasItemsInCart"
              class="layout-new-checkout__element layout-new-checkout__element-small">
              <ToggleButtonGroup
                :options="orderTypeOptions"
                :model-value="orderTypeStore.orderType"
                name="order-type"
                @update:model-value="handleOrderTypeChange"
              />
              <p v-if="isClickAndCollectActive" class="click-collect-panel__pickup-link">
                {{ $t('clickAndCollectReadyTime') }}
              </p>
            </div>
            <!-- Click and Collect panel -->
            <ClickAndCollectPanel
              v-if="checkoutStateStore.clickAndCollectEnabled && orderTypeStore.isClickAndCollect && shoppingCartStore.hasItemsInCart"
              ref="clickAndCollectPanelRef"
              class="layout-new-checkout__element"
            />
            <!-- Discount code accordion - shown above summary for Click and Collect, or Online on mobile -->
            <div v-if="shoppingCartStore.lineItemsInStock?.length && (isClickAndCollectActive || mobileUpsellVisible)"
              class="layout-new-checkout__element layout-new-checkout__element-small">
              <Accordion heading="Legg til rabattkode" heading-class="checkout__accordion-heading">
                <ShoppingCartDiscounts :discounts="shoppingCartStore.cart?.appliedCoupons || {}"
                  @resetDiscountError="handleResetDiscountError" />
              </Accordion>
            </div>
            <div v-if="shoppingCartStore.cart" class="layout-new-checkout__summary layout-new-checkout__element">
              <ShoppingCartSummary :cart="shoppingCartStore.cart" :error="shoppingCartStore.summaryError || ''"
                :show-checkout-link="true" :on-go-to-checkout="handleGoToCheckout"
                :on-confirm-click-and-collect="handleConfirmClickAndCollect" />
            </div>
            <!-- Discount code accordion - shown below summary for Online on desktop -->
            <div v-if="shoppingCartStore.lineItemsInStock?.length && !isClickAndCollectActive && !mobileUpsellVisible"
              class="layout-new-checkout__element layout-new-checkout__element-small">
              <Accordion heading="Legg til rabattkode" heading-class="checkout__accordion-heading">
                <ShoppingCartDiscounts :discounts="shoppingCartStore.cart?.appliedCoupons || {}"
                  @resetDiscountError="handleResetDiscountError" />
              </Accordion>
            </div>
            <template v-for="(upsellItem, index) in upsellModel" :key="index">
              <ProductsModuleContent v-if="upsellItem.primaryList" :key="index" v-show="mobileUpsellVisible"
                :heading="upsellItem.primaryList.label ?? $t('maybeYouAlsoLike')"
                :search-result-recommend-list="upsellItem" :show-as-column-on-mobile="false"
                :user-authenticated="shoppingCartStore.cart?.isAuthenticated ?? false" :favorite-form-hidden="true"
                :is-loading="isLoading" :hide-sizes="true" :hide-colors="true" :hide-favorites="true"
                :is-quick-add="props.isQuickAdd" :productCount="upsellItem.primaryList.productGroups.length"
                :use-skeleton-loaders="true" :track-view-item-list="mobileUpsellVisible" :track-select-item="true"
                :item-list-id="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :item-list-name="`shoppingcart-${upsellItem.primaryList.algorithm}`"
                :upsell-algorithm="upsellItem.primaryList.algorithm"
                :show-inventory-notification="props.showInventoryNotification"
                container-class="product-list__upsell-small product-list__upsell-small--mobile" />
            </template>
            <ShoppingCartKsps v-if="shoppingCartStore.ksps" :key-selling-points="shoppingCartStore.ksps" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
